if (!customElements.get("sticky-mobile-atc")) {
  customElements.define(
    "sticky-mobile-atc",
    class StickyMobileAtc extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.sectionId = this.dataset.sectionId;
        this.enableMobile = this.dataset.enableMobile === "true";
        this.enableDesktop = this.dataset.enableDesktop === "true";
        this.isNotSocks = this.dataset.notSocks === "true";

        this.tierEl = this.querySelector("[data-sticky-tier]");
        this.priceEl = this.querySelector("[data-sticky-price]");
        this.pairsEl = this.querySelector("[data-sticky-pairs]");
        this.perPairEl = this.querySelector("[data-sticky-per-pair]");
        this.saleEl = this.querySelector("[data-sticky-sale]");
        this.compareEl = this.querySelector("[data-sticky-compare]");
        this.stickyButton = this.querySelector("[data-sticky-submit]");
        this.stickyButtonLabel = this.querySelector(
          "[data-sticky-submit-label]",
        );
        this.spinner = this.querySelector(".loading__spinner");

        this.productInfo =
          this.closest("product-info") ||
          document.getElementById(`MainProduct-${this.sectionId}`);
        this.submitButton =
          document.getElementById(this.dataset.submitButtonId) ||
          this.productInfo?.querySelector(".product-form__submit");
        this.bundle = this.isNotSocks
          ? null
          : this.productInfo?.querySelector("bundle-volume-offer");

        this.mobileQuery = window.matchMedia("(max-width: 749px)");
        this.onViewportChange = this.onViewportChange.bind(this);
        this.onStickyClick = this.onStickyClick.bind(this);
        this.onTierChange = this.onTierChange.bind(this);
        this.syncFromSources = this.syncFromSources.bind(this);

        this.stickyButton?.addEventListener("click", this.onStickyClick);
        this.mobileQuery.addEventListener("change", this.onViewportChange);
        this.productInfo?.addEventListener(
          "bundle:tierchange",
          this.onTierChange,
        );

        this.variantChangeUnsubscriber = subscribe?.(
          PUB_SUB_EVENTS.variantChange,
          () => {
            requestAnimationFrame(this.syncFromSources);
          },
        );

        if (this.submitButton) {
          this.buttonObserver = new MutationObserver(() =>
            this.syncButtonState(),
          );
          this.buttonObserver.observe(this.submitButton, {
            attributes: true,
            attributeFilter: ["disabled", "aria-disabled", "class"],
            childList: true,
            subtree: true,
          });
        }

        this.onViewportChange();
        this.syncFromSources();
        this.syncButtonState();
      }

      disconnectedCallback() {
        this.stickyButton?.removeEventListener("click", this.onStickyClick);
        this.mobileQuery?.removeEventListener("change", this.onViewportChange);
        this.productInfo?.removeEventListener(
          "bundle:tierchange",
          this.onTierChange,
        );
        this.variantChangeUnsubscriber?.();
        this.buttonObserver?.disconnect();
        this.intersectionObserver?.disconnect();
        this.hide();
      }

      isEnabledForViewport() {
        return this.mobileQuery.matches
          ? this.enableMobile
          : this.enableDesktop;
      }

      onViewportChange() {
        this.intersectionObserver?.disconnect();
        this.intersectionObserver = null;

        if (!this.isEnabledForViewport() || !this.submitButton) {
          this.hide();
          return;
        }

        this.intersectionObserver = new IntersectionObserver(
          (entries) => {
            const entry = entries[0];
            if (!entry) return;
            if (entry.isIntersecting) {
              this.hide();
            } else {
              this.show();
            }
          },
          {
            root: null,
            threshold: 0,
            rootMargin: "0px",
          },
        );
        this.intersectionObserver.observe(this.submitButton);
      }

      onStickyClick(event) {
        event.preventDefault();
        if (
          this.stickyButton?.disabled ||
          this.stickyButton?.getAttribute("aria-disabled") === "true"
        ) {
          return;
        }
        this.submitButton?.click();
      }

      onTierChange(event) {
        this.applyTierDetail(event?.detail || {});
      }

      applyTierDetail(detail) {
        const label = detail.label || "";
        const qty = Number(detail.qty || 1);
        const priceText = detail.priceText || "";
        const priceCents = detail.priceCents;
        const perPairText = detail.perPairText || "";
        const compareText = detail.compareText || "";
        const hasCompare = Boolean(detail.hasCompare);
        const productTitle = this.isNotSocks
          ? this.dataset.fallbackLabel || label
          : label;

        if (this.tierEl && productTitle) this.tierEl.textContent = productTitle;

        if (this.priceEl) {
          if (priceText) {
            this.priceEl.textContent = priceText;
          } else if (priceCents != null) {
            this.priceEl.textContent = this.formatMoney(priceCents);
          }
        }

        if (this.isNotSocks) {
          if (this.pairsEl && productTitle) {
            this.pairsEl.textContent = productTitle;
          }
        } else {
          if (this.pairsEl) {
            this.pairsEl.textContent = this.formatPairsLabel(qty || 1);
          }

          if (this.perPairEl && perPairText) {
            this.perPairEl.textContent = perPairText;
          }
        }

        if (this.saleEl) {
          if (priceText) {
            this.saleEl.textContent = priceText;
          } else if (priceCents != null) {
            this.saleEl.textContent = this.formatMoney(priceCents);
          }
        }

        if (this.compareEl) {
          if (hasCompare && compareText) {
            this.compareEl.textContent = compareText;
            this.compareEl.classList.remove("is-empty");
            this.compareEl.removeAttribute("aria-hidden");
          } else {
            this.compareEl.innerHTML = "&nbsp;";
            this.compareEl.classList.add("is-empty");
            this.compareEl.setAttribute("aria-hidden", "true");
          }
        }
      }

      syncFromSources() {
        const selected = this.bundle?.querySelector(
          ".bundle-volume-offer__tier.is-selected",
        );

        if (selected) {
          const qty = Number(selected.dataset.qty || 1);
          const finalPrice = Number(selected.dataset.finalPrice || 0);
          const unitPrice = Number(this.bundle?.dataset.unitPrice || 0);
          const label =
            selected
              .querySelector(".bundle-volume-offer__tier-label")
              ?.textContent.trim() || "";
          const priceText =
            selected.querySelector("[data-tier-sale]")?.textContent.trim() ||
            "";
          const perPairText =
            selected
              .querySelector("[data-tier-per-pair]")
              ?.textContent.trim() || "";
          const compareEl = selected.querySelector("[data-tier-compare]");
          const hasCompare =
            compareEl && !compareEl.classList.contains("is-empty");
          const compareText = hasCompare ? compareEl.textContent.trim() : "";
          const priceCents = finalPrice > 0 ? finalPrice : unitPrice * qty;

          this.applyTierDetail({
            label,
            qty,
            priceText,
            priceCents,
            perPairText,
            compareText,
            hasCompare,
          });
          return;
        }

        const fallbackLabel = this.dataset.fallbackLabel || "";
        const fallbackPrice = Number(this.dataset.fallbackPrice || 0);
        const priceNode = this.productInfo?.querySelector(
          `.price-item--sale, .price__regular .price-item, [id^="price-"] .price-item`,
        );
        const priceText =
          priceNode?.textContent?.trim() ||
          (fallbackPrice ? this.formatMoney(fallbackPrice) : "");

        this.applyTierDetail({
          label: fallbackLabel,
          qty: 1,
          priceText,
          priceCents: fallbackPrice,
          perPairText: this.isNotSocks ? "" : this.formatPerPair(fallbackPrice),
          compareText: "",
          hasCompare: false,
        });
      }

      syncButtonState() {
        if (!this.submitButton || !this.stickyButton) return;

        const disabled =
          this.submitButton.disabled ||
          this.submitButton.getAttribute("aria-disabled") === "true";
        this.stickyButton.disabled = Boolean(this.submitButton.disabled);
        if (disabled) {
          this.stickyButton.setAttribute("aria-disabled", "true");
        } else {
          this.stickyButton.removeAttribute("aria-disabled");
        }

        const loading = this.submitButton.classList.contains("loading");
        this.stickyButton.classList.toggle("loading", loading);
        if (this.spinner) {
          this.spinner.classList.toggle("hidden", !loading);
        }

        const label =
          this.submitButton
            .querySelector("span:not(.sold-out-message)")
            ?.textContent?.trim() || this.submitButton.textContent?.trim();
        if (label && this.stickyButtonLabel && !loading) {
          this.stickyButtonLabel.textContent = label;
        }
      }

      show() {
        this.hidden = false;
        this.classList.add("is-visible");
        document.documentElement.classList.add("sticky-atc-active");
        document.documentElement.classList.toggle(
          "sticky-atc-active--mobile",
          this.mobileQuery.matches,
        );
        document.documentElement.classList.toggle(
          "sticky-atc-active--desktop",
          !this.mobileQuery.matches,
        );
      }

      hide() {
        this.hidden = true;
        this.classList.remove("is-visible");
        document.documentElement.classList.remove(
          "sticky-atc-active",
          "sticky-atc-active--mobile",
          "sticky-atc-active--desktop",
        );
      }

      formatPairsLabel(qty) {
        const template =
          this.dataset.pairsTemplate ||
          window.bundleVolumeStrings?.qtyPairs ||
          "{{ count }} pairs";
        return template.replace(/\{\{\s*count\s*\}\}/g, String(qty));
      }

      formatPerPair(cents) {
        const template =
          this.dataset.perPairTemplate ||
          window.bundleVolumeStrings?.pricePerPair ||
          "{{ price }}/pair";
        return template.replace(
          /\{\{\s*price\s*\}\}/g,
          this.formatMoney(cents),
        );
      }

      formatMoney(cents) {
        const value = (Number(cents) / 100).toFixed(2);
        const format = this.dataset.moneyFormat || "${{amount}}";
        const withComma = value.replace(".", ",");
        const noDecimals = String(Math.round(Number(cents) / 100));
        return format
          .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, withComma)
          .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDecimals)
          .replace(/\{\{\s*amount\s*\}\}/g, value);
      }
    },
  );
}
